import {
  MaxWeightMethod,
  insertUserMaxWeightSchema,
} from "@/api/db/schema/user-max-weights";
import {
  batchWorkoutLogSchema,
  insertWorkoutLogSchema,
  patchWorkoutLogSchema,
  upsertWorkoutLogSchema,
} from "@/api/db/schema/workout-logs";
import { isCoachingClient } from "@/api/services/coaching-client.service";
import { objectService } from "@/api/services/object.service";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

// =====================
// USER MAX WEIGHTS
// =====================

export const getUserMaxWeightsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    const { userMaxService } = await import("@/api/services/user-max.service");
    const maxWeights = await userMaxService.getUserMaxWeights(
      context.auth.userId,
    );

    return {
      status: 200,
      body: {
        maxWeights,
        total: maxWeights.length,
      },
    };
  });

// Get effective max weights (manual or RPE-estimated)
const getEffectiveMaxWeightsSchema = z.object({
  exerciseIds: z.array(z.string()),
});

export const getEffectiveMaxWeightsFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(getEffectiveMaxWeightsSchema)
  .handler(async ({ context, data }) => {
    const { userMaxService } = await import("@/api/services/user-max.service");
    const maxWeights = await userMaxService.getEffectiveMaxWeights(
      context.auth.userId,
      data.exerciseIds,
    );

    return {
      status: 200,
      body: {
        maxWeights,
        total: maxWeights.length,
      },
    };
  });

const getMaxForExerciseSchema = z.object({
  exerciseId: z.string(),
});

export const getMaxForExerciseFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getMaxForExerciseSchema)
  .handler(async ({ context, data }) => {
    const { userMaxService } = await import("@/api/services/user-max.service");
    const maxWeight = await userMaxService.getMaxForExercise(
      context.auth.userId,
      data.exerciseId,
    );

    return {
      status: 200,
      body: maxWeight,
    };
  });

export const setUserMaxWeightFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertUserMaxWeightSchema)
  .handler(async ({ context, data }) => {
    const { userMaxService } = await import("@/api/services/user-max.service");
    const maxWeight = await userMaxService.setMaxWeight(
      context.auth.userId,
      data,
    );

    return {
      status: 200,
      body: {
        message: "Max ağırlık güncellendi",
        maxWeight,
      },
    };
  });

// Delete max weight
const deleteMaxWeightSchema = z.object({
  maxId: z.string(),
});

export const deleteUserMaxWeightFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteMaxWeightSchema)
  .handler(async ({ context, data }) => {
    const { userMaxService } = await import("@/api/services/user-max.service");
    const maxWeight = await userMaxService.deleteMaxWeight(data.maxId);

    if (!maxWeight) {
      return {
        status: 404,
        body: { message: "Max ağırlık bulunamadı" },
      };
    }

    // Verify ownership
    if (maxWeight.userId !== context.auth.userId) {
      return {
        status: 403,
        body: { message: "Bu kayda erişim yetkiniz yok" },
      };
    }

    return {
      status: 200,
      body: { message: "Max ağırlık silindi" },
    };
  });

// Estimate 1RM from reps
const estimate1RMSchema = z.object({
  weight: z.number().positive(),
  reps: z.number().int().min(1).max(20),
  exerciseId: z.string().optional(),
  save: z.boolean().optional(),
});

export const estimate1RMFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(estimate1RMSchema)
  .handler(async ({ context, data }) => {
    const { userMaxService } = await import("@/api/services/user-max.service");
    const estimated = userMaxService.estimate1RM(data.weight, data.reps);

    // Optionally save as user's max
    if (data.save && data.exerciseId) {
      await userMaxService.setMaxWeight(context.auth.userId, {
        exerciseId: data.exerciseId,
        maxWeight: estimated,
        method: MaxWeightMethod.ESTIMATED,
      });
    }

    return {
      status: 200,
      body: {
        estimated1RM: estimated,
        saved: data.save && data.exerciseId,
      },
    };
  });

// Save estimated 1RM from calculator
const saveEstimated1RMSchema = z.object({
  exerciseId: z.string(),
  weight: z.number().positive(),
  reps: z.number().int().min(1).max(20),
  notes: z.string().optional(),
});

export const saveEstimated1RMFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(saveEstimated1RMSchema)
  .handler(async ({ context, data }) => {
    const { userMaxService } = await import("@/api/services/user-max.service");
    const estimated1RM = userMaxService.estimate1RM(data.weight, data.reps);

    const maxWeight = await userMaxService.setMaxWeight(context.auth.userId, {
      exerciseId: data.exerciseId,
      maxWeight: estimated1RM,
      method: MaxWeightMethod.ESTIMATED,
      notes:
        data.notes ?? `Estimated from ${data.weight}kg × ${data.reps} reps`,
    });

    return {
      status: 200,
      body: {
        message: "Tahmini 1RM kaydedildi",
        maxWeight,
        estimated1RM,
      },
    };
  });

// =====================
// WORKOUT LOGS
// =====================

export const logWorkoutSetFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertWorkoutLogSchema)
  .handler(async ({ context, data }) => {
    const { userProgramService } = await import(
      "@/api/services/user-program.service"
    );
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );

    // Verify active program
    const activeProgram = await userProgramService.getUserActiveProgram(
      context.auth.userId,
    );

    if (!activeProgram || activeProgram.programId !== data.programId) {
      return {
        status: 403,
        body: {
          message:
            "Bu programa ait log giremezsiniz. Lütfen önce programı aktif edin.",
        },
      };
    }

    const log = await workoutLogService.logSet(context.auth.userId, data);

    return {
      status: 201,
      body: {
        message: "Set kaydedildi",
        log,
      },
    };
  });

// Upsert workout set - prevents duplicate records
export const upsertWorkoutSetFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(upsertWorkoutLogSchema)
  .handler(async ({ context, data }) => {
    const { userProgramService } = await import(
      "@/api/services/user-program.service"
    );
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );

    // Verify active program
    const activeProgram = await userProgramService.getUserActiveProgram(
      context.auth.userId,
    );

    if (!activeProgram || activeProgram.programId !== data.programId) {
      return {
        status: 403,
        body: {
          message:
            "Bu programa ait log giremezsiniz. Lütfen önce programı aktif edin.",
        },
      };
    }

    const log = await workoutLogService.upsertSet(context.auth.userId, data);

    return {
      status: 200,
      body: {
        message: "Kaydedildi",
        log,
      },
    };
  });

export const logWorkoutBatchFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(batchWorkoutLogSchema)
  .handler(async ({ context, data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const logs = await workoutLogService.logBatch(context.auth.userId, data);

    return {
      status: 201,
      body: {
        message: `${logs.length} set kaydedildi`,
        logs,
      },
    };
  });

export const getUserLogsSchema = z.object({
  programId: z.string().optional(),
  dayId: z.string().optional(),
  exerciseId: z.string().optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  limit: z.number().int().min(1).max(100).optional(),
});

export const getUserLogsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getUserLogsSchema)
  .handler(async ({ context, data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const logs = await workoutLogService.getUserLogs(context.auth.userId, data);

    return {
      status: 200,
      body: {
        logs,
        total: logs.length,
      },
    };
  });

const getLogsForDaySchema = z.object({
  programId: z.string(),
  dayId: z.string(),
  date: z.coerce.date().optional(),
});

export const getLogsForDayFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getLogsForDaySchema)
  .handler(async ({ context, data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const logs = await workoutLogService.getLogsForDay(
      context.auth.userId,
      data.programId,
      data.dayId,
      data.date,
    );

    return {
      status: 200,
      body: {
        logs,
        total: logs.length,
      },
    };
  });

export const getRecentLogsSchema = z.object({
  exerciseId: z.string(),
  limit: z.number().int().min(1).max(50).optional(),
});

export const getRecentLogsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getRecentLogsSchema)
  .handler(async ({ context, data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const logs = await workoutLogService.getRecentLogsForExercise(
      context.auth.userId,
      data.exerciseId,
      data.limit ?? 10,
    );

    return {
      status: 200,
      body: {
        logs,
        total: logs.length,
      },
    };
  });

export const updateLogFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchWorkoutLogSchema)
  .handler(async ({ data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const { logId, ...updateData } = data;
    const log = await workoutLogService.updateLog(logId, updateData);

    if (!log) {
      return {
        status: 404,
        body: { message: "Kayıt bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Kayıt güncellendi",
        log,
      },
    };
  });

const deleteLogSchema = z.object({
  logId: z.string(),
});

export const deleteLogFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteLogSchema)
  .handler(async ({ data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const log = await workoutLogService.deleteLog(data.logId);

    if (!log) {
      return {
        status: 404,
        body: { message: "Kayıt bulunamadı" },
      };
    }

    // Log'a bağlı video varsa S3 + object tablosundan da temizle
    if (log.videoUrl) {
      const extractKey = (url: string) => {
        const match = url.match(/workout-sets\/[^?#]+/);
        return match ? match[0] : url;
      };
      const videoKey = extractKey(log.videoUrl);
      const thumbKey = log.videoThumbnailUrl
        ? extractKey(log.videoThumbnailUrl)
        : undefined;

      // Non-fatal: silme hatası log silmeyi engellemez
      objectService.deleteObject(videoKey).catch((_e) => {
        /* ignore */
      });
      if (thumbKey) {
        objectService.deleteObject(thumbKey).catch((_e) => {
          /* ignore */
        });
      }
    }

    return {
      status: 200,
      body: { message: "Kayıt silindi" },
    };
  });

// Summary and Stats

export const getSummarySchema = z.object({
  programId: z.string().optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
});

export const getWorkoutSummaryFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getSummarySchema)
  .handler(async ({ context, data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const summary = await workoutLogService.getWorkoutSummary(
      context.auth.userId,
      data,
    );

    return {
      status: 200,
      body: summary,
    };
  });

const getPersonalRecordsSchema = z.object({
  exerciseId: z.string(),
});

export const getPersonalRecordsFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(getPersonalRecordsSchema)
  .handler(async ({ context, data }) => {
    const { workoutLogService } = await import(
      "@/api/services/workout-log.service"
    );
    const records = await workoutLogService.getPersonalRecords(
      context.auth.userId,
      data.exerciseId,
    );

    return {
      status: 200,
      body: records,
    };
  });

// =====================
// VIDEO UPLOADS
// =====================

const generateVideoUploadUrlSchema = z.object({
  fileName: z.string(),
  fileSize: z.number(),
  contentType: z.string(),
});

export const generateVideoUploadUrlFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(generateVideoUploadUrlSchema)
  .handler(async ({ context, data }) => {
    const isClient = await isCoachingClient(context.auth.userId);
    if (context.auth.role !== "admin" && !isClient) {
      return {
        status: 403,
        body: { message: "Bu işlem için yetkiniz yok." },
      };
    }

    // Validate file size (200MB max for video)
    const MAX_SIZE = 200 * 1024 * 1024;
    if (data.fileSize > MAX_SIZE) {
      return {
        status: 400,
        body: { message: "Video çok büyük. Maksimum 200MB." },
      };
    }

    const extension = data.fileName.split(".").pop() || "mp4";
    const { key } = objectService.generateKey(extension);
    const fullKey = `workout-sets/${key}`;

    const signedUrl = await objectService.createObjectUrl(
      fullKey,
      data.fileSize,
      data.contentType,
    );

    const { env } = await import("@/api/lib/types");
    return {
      status: 200,
      body: {
        url: signedUrl,
        key: fullKey,
        publicUrl: `${env.BUCKET_URL}/${fullKey}`,
      },
    };
  });

// =====================
// VIDEO DELETE
// =====================

const deleteVideoSchema = z.object({
  // Full S3 key, e.g. "workout-sets/xxx.mp4"
  // Also accepts the full CDN URL — key is extracted server-side
  videoKey: z.string(),
  // Optional: also clear the thumbnail (key or URL)
  thumbnailKey: z.string().optional(),
});

export const deleteVideoFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteVideoSchema)
  .handler(async ({ context, data }) => {
    const isClient = await isCoachingClient(context.auth.userId);
    if (context.auth.role !== "admin" && !isClient) {
      return { status: 403, body: { message: "Bu işlem için yetkiniz yok." } };
    }

    // Accept both full CDN URL and bare key
    const extractKey = (raw: string) => {
      const match = raw.match(/workout-sets\/[^?#]+/);
      return match ? match[0] : raw;
    };

    const videoKey = extractKey(data.videoKey);
    const thumbKey = data.thumbnailKey
      ? extractKey(data.thumbnailKey)
      : undefined;

    try {
      await objectService.deleteObject(videoKey);
    } catch (_e) {
      // Non-fatal: object may not exist in DB, still attempt S3 delete
    }

    if (thumbKey) {
      try {
        await objectService.deleteObject(thumbKey);
      } catch (_e) {
        // Non-fatal
      }
    }

    return { status: 200, body: { message: "Video silindi." } };
  });

// =====================
// VIDEO TRIM JOB
// =====================

const triggerVideoTrimSchema = z.object({
  logId: z.string(),
  sourceKey: z.string(),
  trimStart: z.number(),
  trimEnd: z.number(),
  duration: z.number(),
});

export const triggerVideoTrimFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(triggerVideoTrimSchema)
  .handler(async ({ context, data }) => {
    const isClient = await isCoachingClient(context.auth.userId);
    if (context.auth.role !== "admin" && !isClient) {
      return { status: 403, body: { message: "Bu işlem için yetkiniz yok." } };
    }

    const { SQSClient, SendMessageCommand } = await import(
      "@aws-sdk/client-sqs"
    );
    const { getResource } = await import("../../api/lib/resources");

    const sqs = new SQSClient();
    await sqs.send(
      new SendMessageCommand({
        QueueUrl: getResource.videoTrimQueueUrl,
        MessageBody: JSON.stringify({
          logId: data.logId,
          sourceKey: data.sourceKey,
          trimStart: data.trimStart,
          trimEnd: data.trimEnd,
          duration: data.duration,
        }),
      }),
    );

    return { status: 200, body: { message: "Video işleme kuyruğa alındı." } };
  });
